import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, Trash2, Pause, Play, Loader2, Mail, MessageSquare, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function badgeFreq(freq) {
  if (freq === "instant") return { label: "Instantly", color: "bg-[#00E5FF]/20 text-[#00E5FF] border-[#00E5FF]/30" };
  if (freq === "daily") return { label: "Daily digest", color: "bg-[#7C3AED]/20 text-[#A78BFA] border-[#7C3AED]/30" };
  if (freq === "weekly") return { label: "Weekly digest", color: "bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30" };
  return { label: freq, color: "bg-white/10 text-white/70 border-white/10" };
}

function summarizeFilters(filters = {}) {
  const parts = [];
  if (filters.listing_type) parts.push(String(filters.listing_type).replace("-", " "));
  if (filters.make) parts.push(filters.make);
  if (filters.model) parts.push(filters.model);
  if (filters.property_type) parts.push(filters.property_type);
  if (filters.min_bedrooms || filters.min_beds) parts.push(`${filters.min_bedrooms || filters.min_beds}+ bd`);
  if (filters.min_bathrooms || filters.min_baths) parts.push(`${filters.min_bathrooms || filters.min_baths}+ ba`);
  if (filters.min_price) parts.push(`from $${Number(filters.min_price).toLocaleString()}`);
  if (filters.max_price) parts.push(`up to $${Number(filters.max_price).toLocaleString()}`);
  if (filters.city) parts.push(`in ${filters.city}`);
  if (filters.state) parts.push(filters.state);
  return parts.length ? parts.join(" • ") : "All matching listings";
}

export default function DashboardSavedSearches() {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("auth_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/v1/saved-searches`, { headers });
      setItems(r.data?.items || []);
    } catch (e) {
      console.error(e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated]);

  const togglePause = async (item) => {
    const next = item.status === "paused" ? "active" : "paused";
    try {
      await axios.patch(`${API}/v1/saved-searches/${item.id}`, { status: next }, { headers });
      toast.success(next === "paused" ? "Paused" : "Resumed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    }
  };

  const remove = async (item) => {
    if (!confirm(`Delete "${item.name}"? You can always create a new alert from the marketplace.`)) return;
    try {
      await axios.delete(`${API}/v1/saved-searches/${item.id}`, { headers });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="saved-searches-panel">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Bell className="w-6 h-6 text-[#00E5FF]" />
          Saved Searches & Alerts
        </h1>
        <p className="text-sm text-[#94A3B8] mt-1">
          Get notified by email or SMS the moment a matching listing goes live.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white/5 border border-white/10 rounded-2xl">
          <Search className="w-10 h-10 text-white/30 mx-auto mb-3" />
          <p className="text-white font-semibold">No saved searches yet</p>
          <p className="text-sm text-[#94A3B8] mt-1 mb-4">
            Save a search from the marketplace and we&apos;ll alert you when matches go live.
          </p>
          <a
            href="/marketplace"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] text-white text-sm font-bold"
            data-testid="saved-searches-empty-cta"
          >
            Browse the marketplace
          </a>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const f = badgeFreq(item.frequency);
            const channels = item.channels || ["email"];
            return (
              <div
                key={item.id}
                className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                data-testid={`saved-search-row-${item.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-white font-semibold truncate">{item.name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${f.color}`}>{f.label}</span>
                      {item.status === "paused" && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full border bg-yellow-500/10 text-yellow-300 border-yellow-500/30">
                          Paused
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#94A3B8] mt-1">{summarizeFilters(item.filters)}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-[#64748B]">
                      {channels.includes("email") && (
                        <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {item.email}</span>
                      )}
                      {channels.includes("sms") && item.phone && (
                        <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {item.phone}</span>
                      )}
                      <span>· {item.notifications_sent || 0} alerts sent</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => togglePause(item)}
                      className="h-8 px-3 border-white/20 text-white hover:bg-white/10"
                      data-testid={`saved-search-toggle-${item.id}`}
                    >
                      {item.status === "paused" ? (
                        <><Play className="w-3.5 h-3.5 mr-1" /> Resume</>
                      ) : (
                        <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => remove(item)}
                      className="h-8 px-3 border-red-500/30 text-red-300 hover:bg-red-500/10"
                      data-testid={`saved-search-delete-${item.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
